// The card table.
//
// A pure module: an effect per card, taking the already-cloned state. Nothing
// here may read the clock or generate a random number — a card that resolved
// differently on a replay is the same class of bug as an unordered map
// iteration, and it would be found the same way, months later, as a hash that
// does not match.
//
// Cards are *not* moves. They do not cost the turn and there is no per-turn
// cap: they are the single-player equivalent of a belt command. Playing a
// Gambit and the Reactor Boosters in the same turn is legal, and is a good
// turn.

import {
  CLASS_MONARCH,
  CLASS_POWER,
  type Direction,
  KING,
  KING_DIRS,
  makePiece,
  pieceColor,
  pieceType,
  QUEEN,
  type Side
} from "../board";
import {
  addKing,
  areEnemies,
  armourLeft,
  canSkipTurn,
  disguisedAs,
  hasCell,
  invalidateMoves,
  isMatriarch,
  KING_FACTORY,
  livingSides,
  pieceAt,
  pieceClassAt,
  type Position,
  reframePlating,
  setCells,
  setClasp,
  setFacing,
  setPiece,
  setSkipNext,
  setVariant,
  variantAt
} from "../position";
import { removeSquare } from "../removeSquare";
import { hasStructure } from "../industry/state";
import { payVariant, upgradeCostError } from "../industry/upgrades";
import { manifoldSquares, refuseManifold } from "./manifold";
import { placeMoat, refuseMoat } from "./moat";
import { LOGISTICS, MATRIARCH, PIECE_PLATING, RULES, VARIANT_PLAIN } from "../rules";
import { movesForSide } from "./titan";
import { reservedSquares, touches } from "../industry/structures";
import type { GameState } from "../commands";
import { spendTicks, withOffTurnOnce } from "./ticks";
import { wearDisguise } from "./disguise";

/** Every card. Ordinals are not the wire format here — the id is. */
export const CARDS = [
  "reactor-boosters",
  "full-production-gambit",
  "stasis-field",
  "micro-singularity-bomb",
  "programmer-oil",
  "bridges",
  "signet-crown",
  "hydraulic-clasp",
  "iff-spoofer",
  "time-fluxxer",
  "yearning-for-the-mines",
  "coronation",
  "spatial-manifold",
  "static-moat",
  "the-enemy-is-at-the-gates",
  "march"
] as const;
export type CardId = (typeof CARDS)[number];

/**
 * Ids that used to name a card, and the card they name now.
 *
 * **A rename keeps the mechanism and changes the id** (§26.1 (12)) — and the
 * id is the wire format, so a run saved holding the old one comes back naming
 * a card that no longer exists. That is not a missing card: every reader looks
 * the id up and reads the entry straight off, so `CARD_TABLE[old]` is
 * `undefined` and the hand renders `undefined.name` — a white screen on
 * sight, before the player can even click it. Exactly the failure retiring
 * Load Em Up caused, arriving through the *save* door rather than the opening
 * command's, which is the only one that was ever guarded.
 *
 * **An alias rather than dropping the card**, which is the other honest
 * answer and the one `newGame` takes for an id nothing can resolve. The
 * difference is that this id *can* be resolved: the card is still here and
 * still does the same thing, so silently taking it out of somebody's deck
 * spends a prize they earned to save us a line. Dropping stays the right
 * answer for a card that is genuinely gone.
 *
 * **Resolved where a stored run enters the game and nowhere else** — see
 * `runSave.ts`, which applies it *after* the corruption check, since the old
 * id is what was hashed when the save was written. Nothing in the reducer
 * consults this: by the time a command is applied the ids are current, so the
 * rules have exactly one name for each card and no legacy branch.
 */
export const CARD_ALIASES: Readonly<Record<string, CardId>> = {
  // §26 C6: Matriarch -> Signet Crown. The queen it makes is still a Matriarch.
  matriarch: "signet-crown",
  // §26 C1: Triple Time -> Reactor Boosters. The *arithmetic* moved with the
  // name (a target became an increment), which the alias deliberately says
  // nothing about: a card in an old deck has not been played yet, so what it
  // does is decided when it is played, by the entry it now resolves to.
  "triple-time": "reactor-boosters",
  // §26 C3: Productivity Gambit -> Full Production Gambit. Exhaustion became a
  // counted dozen; an unplayed card is decided by the entry it resolves to now.
  "productivity-gambit": "full-production-gambit",
  // §26 C4: Iron Armor -> Stasis Field, widened to anybody's piece and given a threat rule.
  "iron-armor": "stasis-field",
  // §26 C7: Black Hole -> Micro-Singularity Bomb, which aims from a piece and takes what is there.
  "black-hole": "micro-singularity-bomb",
  // §26 C8: Long Socks -> Programmer Oil, which gained a queen gate with the name.
  "long-socks": "programmer-oil",
  // §26 C9: Free Space -> Bridges. Paving stopped growing the board and started joining it.
  "free-space": "bridges",
  // §26 C11: Imitate -> Hydraulic Clasp. Neither a rename nor a drop but the
  // third case, a **replacement** — the mechanism is gone. Aliased even so,
  // because what carries forward is the *prize*: the Mirror still pays out one
  // card, and this is the card it pays out now.
  imitate: "hydraulic-clasp",
  // §26 C12: Disguise -> IFF Spoofer. The belief became a rule with the name.
  disguise: "iff-spoofer",
  // §31 C11: MegaO-13 -> Time Fluxxer, which rewinds a round (`rewind.ts`).
  "mega-o-13": "time-fluxxer"
};

/** Why a card cannot be played right now, or null. */
export type CardRefusal =
  | "not-a-run"
  | "not-in-hand"
  | "game-over"
  | "needs-a-target"
  | "not-your-piece"
  // The Stasis Field reaches anybody's piece, so a bare square is all that is left to refuse.
  | "no-piece-there"
  | "not-a-king"
  | "not-a-queen"
  // Coronation's three (§31 C3): only a Power piece is crowned.
  | "not-a-power-piece"
  | "already-royal"
  | "no-monarch-beside"
  | "already-crowned"
  | "already-armoured"
  // The Spoofer's: two squares, and the second one is bare. Named rather than
  // shared, because a shared refusal is a control that looks live and says
  // nothing — the trap this project keeps paying for.
  | "nothing-to-copy"
  // The Hydraulic Clasp. Two sentences, because they ask for different acts:
  // wait for a lock's clock, or drop the clamp you already have.
  | "not-beside-it"
  | "already-locked"
  | "already-clasped"
  // The Spoofer. An ally's colours buy nothing and your own are a
  // costume with no wearer, so the card needs a line to cross and says so when
  // there is not one.
  | "not-an-enemy"
  | "already-disguised"
  // The Full Production Gambit alone on the board: no next turn to give up.
  | "no-turn-to-skip"
  // The Time Fluxxer, which is not played through the reducer at all — a rewind is an
  // operation on the log and `apply` has only a state. The card answers this
  // to every play so the ordinary door fails loudly instead of spending it on
  // a board that does not move.
  | "rewind-only"
  // The Micro-Singularity Bomb (§26 C7), which retired `not-bare` and
  // `not-your-ground` with the old Black Hole's questions.
  | "not-a-square"
  | "no-piece-beside"
  | "never-a-monarch"
  | "objective-square"
  // Programmer Oil (§31 C6): Robotic Monarchies only.
  | "not-robotic"
  // The Enemy is at the Gates (§31 C4): points that scale by enemies need one.
  | "no-enemies"
  // The Spatial Manifold names one edge (§31 C7).
  | "needs-a-direction"
  // The Spatial Manifold (§26 C10). All three are about the push itself, since
  // the card takes no target: past the packing on every side, blocked in every
  // column, or a new line of ground joining a ray onto your own king.
  // `not-an-edge` went with the direction-picking gesture that produced it.
  | "edge-of-the-world"
  | "nothing-to-grow"
  | "opens-a-line"
  // Static Moat (§26 C13); `not-a-square` above (the Bomb's) is reused too.
  | "square-occupied"
  | "already-moated"
  | "not-a-chain"
  | "moat-too-wide"
  // The Signet Crown, refused on a queen whose row traded the factory away.
  | "no-factory"
  // The Signet Crown's upgrade half (§31 C5, P3): site, bill and fuel.
  | "no-armory"
  | "out-of-armory-reach"
  | "too-poor"
  | "no-fuel";

export interface CardPlay {
  /** A square the card acts on, for the cards that need one. */
  target?: number;
  /** The pieces a card names, and a square — the Static Moat's wall (§26 C13). */
  pieces?: number[];
  destination?: number;
  /**
   * The other end of a two-square card: the Hydraulic Clasp's partner, or the
   * army the IFF Spoofer is copying.
   *
   * A second square, because those cards are about a *relationship* — this
   * piece, that piece — and one target could only mean "pair with whatever
   * happens to be nearest", which is a rule nobody could read off the board.
   */
  source?: number;
  /** The Spatial Manifold's edge (§31 C7). */
  direction?: Direction;
}

export interface Card {
  id: CardId;
  name: string;
  /**
   * The terse line on the card's own face — what it does and nothing else,
   * in words short enough that the face never has to clip them.
   */
  short: string;
  /** The fuller account, a few lines at most: the card's hover card. */
  text: string;
  /** True when the player must pick a square before it can be played. */
  targeted: boolean;
  /**
   * Played and kept, rather than played and gone.
   *
   * This is what the deck is *for*: without it every card is a one-shot and
   * "deck" is a synonym for "the cards you have not burned yet". A reusable
   * card leaves your hand for the turn and is dealt again with the next one.
   *
   * Choosing which is a balance question with one hard edge: a reusable card
   * is available every turn for the rest of the run, so anything that grants
   * tempo or material cannot be one. Three moves a turn forever, or the whole
   * economy run to exhaustion every turn, is not a strong card — it is the end
   * of the game as a thing with decisions in it.
   *
   * **Nothing sets it just now.** Load Em Up was the only card that passed
   * that edge, and it retired when belts started carrying pieces for free.
   * Kept, with the reducer branch it drives, because the edge is the design
   * decision and the next card that clears it should not have to re-derive it.
   */
  reusable?: boolean;
  /**
   * Playing it costs the turn, like a move or a forge.
   *
   * The default is that cards are free — they are the single-player equivalent
   * of a belt command, and a hand you cannot use without giving up your move
   * is a hand you rarely use. A card that *resolves the whole economy* is the
   * exception: it is doing the work a turn is for.
   *
   * `true` costs one move, `"turn"` costs the whole turn however many are left
   * — one field with the degree named rather than a second boolean beside it,
   * because two booleans can be set at once and this cannot: a card either
   * spends a move, spends the turn, or is free.
   */
  spendsTurn?: boolean | "turn";
  /** Why this play is refused, or null. The reducer asks before applying. */
  refuse(state: GameState, play: CardPlay): CardRefusal | null;
  /** Mutates the cloned state. Only reached when `refuse` returned null. */
  apply(state: GameState, play: CardPlay): void;
}

/**
 * Two more moves in this turn, and a second copy is two more again.
 *
 * **`+`, not `max`, and the reversal is the unit** (§26 C1). The old card set
 * the turn *to* three moves, which is two different cards depending on what
 * else you were holding: three on an ordinary turn, one on a Titan's two-move
 * turn, and **nothing at all** on a second play, since `max(3, 3)` is 3. A
 * grant that quietly shrinks as the run gets stronger is the opposite of a
 * prize, and the old reason for the clamp — that stacking is "a combination
 * that ends the run" — is answered by `maxMovesPerTurn`, which bounds the
 * turn however the moves were come by, and by the cards being *spent*: a
 * second one costs a second prize.
 *
 * Nothing needs cleaning up afterwards — End Turn resets `movesLeft` to the
 * next holder's allowance when the clock passes, so the grant expires by construction
 * and cannot leak into the next turn or the opponent's. That is also why this
 * writes `movesLeft` and never `movesPerTurn`: the field it adds to is the
 * one the clock throws away.
 */
/*
 * **It will have a speedup icon.** The card is about *tempo* and nothing else,
 * and tempo is the one thing on this board with no visual language yet —
 * material has boxes, direction has chevrons, ground has tiles. A fast-forward
 * glyph is the obvious reading and it wants to be the same mark wherever
 * tempo appears: on the card, on the pip, and on whatever says "three moves
 * left" during the turn it is spent.
 */
const reactorBoosters: Card = {
  id: "reactor-boosters",
  name: "Reactor Boosters",
  short: "Two extra moves this turn",
  text: "Take two more moves this turn. Your factory ticks with each of them.",
  targeted: false,
  refuse: () => null,
  apply: (state) => {
    state.position.movesLeft = Math.min(
      RULES.maxMovesPerTurn,
      state.position.movesLeft + RULES.reactorBoosterMoves
    );
  }
};

/**
 * **MARCH** (§33 T5): +`LOGISTICS.march` Army Logistics this turn — that many
 * more Fodder Moves for every Power Move the side's turn holds. Written into
 * `fodderLeft`, which End Turn refills, so it expires by construction like
 * Reactor Boosters' grant.
 */
const march: Card = {
  id: "march",
  name: "MARCH",
  short: "+9 Army Logistics this turn",
  text: "Each Power Move this turn brings nine more Fodder Moves.",
  targeted: false,
  refuse: () => null,
  apply: (state) => {
    const { position, industry } = state;
    position.fodderLeft += LOGISTICS.march * movesForSide(position, industry, position.turn);
  }
};

/** Is one of this side's pieces on one of the eight squares around `at`? */
const ownPieceBeside = (pos: Position, at: number, side: Side): boolean =>
  KING_DIRS.some((offset) => {
    const sq = at + offset;
    // `touches`, never the offset alone: an offset at the file edge carries into the rank.
    if (!touches(sq, at)) return false;
    const piece = pieceAt(pos, sq);
    return piece !== 0 && pieceColor(piece) === side;
  });

/** Take a square beside you off the board with everything on it. Rules and why: §26 C7. */
const microSingularityBomb: Card = {
  id: "micro-singularity-bomb",
  name: "Micro-Singularity Bomb",
  short: "Erase a square and all on it",
  text:
    "Destroy a square touching one of your pieces, and everything on it. " +
    "It does not come back.",
  targeted: true,
  refuse: (state, play) => {
    if (play.target === undefined) return "needs-a-target";
    const side = state.run!.side;
    const at = play.target;
    // A cell or a building's footprint: a structure leaves `cells`, so `hasCell` alone misses one.
    if (!hasCell(state.position, at) && !hasStructure(state.industry, at)) {
      return "not-a-square";
    }
    if (!ownPieceBeside(state.position, at, side)) return "no-piece-beside";
    const piece = pieceAt(state.position, at);
    // Born, crowned or a Matriarch: `pieceClassAt` is the one answer to "is
    // this royalty", and it is the class rather than the rank because a
    // Matriarch is a Monarch that is not a `KING` (§26.1 (10)).
    if (piece !== 0 && pieceClassAt(state.position, at) === CLASS_MONARCH) {
      return "never-a-monarch";
    }
    if (reservedSquares(state).includes(at)) return "objective-square";
    return null;
  },
  // `removeSquare` sweeps every square-keyed Map by reflection, so nothing orphans.
  apply: (state, play) => {
    removeSquare(state.position, state.industry, play.target!);
  }
};

/**
 * End your turn now for a dozen ticks, and give up the next one (§26 C3).
 *
 * Unilateral: PLAN §3.6's Gambit is a negotiation and there is nobody to
 * negotiate with, so the card grants the half you own. The two-player offer
 * is retired (§31 R4).
 * A counted dozen where this used to run to *exhaustion*, so it is worth the
 * same on every board. Reasoning in PLAN-MECHANICS §26 C3.
 */
const fullProductionGambit: Card = {
  id: "full-production-gambit",
  name: "Full Production Gambit",
  short: `${RULES.fullProductionTicks} factory ticks, then skip a turn`,
  text:
    `End your turn now and run your factory ${RULES.fullProductionTicks} times. ` +
    "You skip your next turn.",
  // The whole turn goes, not one move of it: the reducer runs this through
  // `endTurn` (`clock.ts`), the same body the `pass` command ends a turn with.
  spendsTurn: "turn",
  targeted: false,
  refuse: (state) => {
    // The price has to be payable: `endTurn` leaves an unspendable skip *set*.
    if (!canSkipTurn(state.position, state.run!.side)) return "no-turn-to-skip";
    return null;
  },
  apply: (state) => {
    const side = state.run!.side;
    setSkipNext(state.position, side, true);
    spendTicks(state, side, RULES.fullProductionTicks);
  }
};

/**
 * Freeze any one piece where it stands, three rounds (§26 C4, §31 C2).
 *
 * Untakeable (`canBeHurt`); immovable (`generateMoves`, `generateCastles`);
 * threatens nothing (`attacks.ts`); blocks rays and leaps (`leapCrosses`).
 * **Monarchs included** (§31 C2): a frozen royal binds nothing
 * (`bindingRoyal`), so it is never in check while the lock holds.
 */
const stasisField: Card = {
  id: "stasis-field",
  name: "Stasis Field",
  short: "Freeze a piece in place",
  text:
    "Lock any one piece in place for three rounds, whoever owns it — kings too. " +
    "It cannot move, take, threaten, be taken or be jumped over.",
  targeted: true,
  refuse: (state, play) => {
    if (play.target === undefined) return "needs-a-target";
    const piece = pieceAt(state.position, play.target);
    // No ownership test — the change. `playCard` asks this per square, so it also lights the target.
    if (!piece) return "no-piece-there";
    if (armourLeft(state.position, play.target) > 0) return "already-armoured";
    return null;
  },
  apply: (state, play) => {
    // An expiry, not a counter (`minedAt`'s shape), in rounds (§31 T).
    // The one `armour` write outside `position.ts`, so it invalidates by hand.
    invalidateMoves(state.position);
    state.position.armour.set(
      play.target!,
      state.position.round + RULES.stasisRounds
    );
  }
};

// **Load Em Up is retired.** Belts carry their owner's pieces every tick, for
// every side, in a two-player game as much as in a run — `industry/carry.ts`
// holds the rule and `runFlow` runs it. A card granting what the game already
// does is a card that does nothing, and the id is gone rather than left in
// `CARDS` reading as an unfinished one. What it was worth is still worth
// something: it was bounded by how much lane you had built, and so is this.


/**
 * Kings move — and attack — like queens, for three turns.
 *
 * The card is Programmer Oil; the mechanism is still Long Socks (§26 C8).
 *
 * **Per side, and both halves together.** The slide is generated in `moveGen`
 * and answered in `isAttackedByEnemyOf`, because captures come out of move
 * generation: a king that slid across the board to take something nobody
 * counted as attacked would be capturing out of nowhere, and the enemy would
 * walk pieces into range, since that query is exactly what "is this square
 * safe" asks. One rule, two call sites.
 *
 * Three things it inherits rather than needs:
 *
 *  - **Perft cannot see it.** `Position.socks` is empty on every board nobody
 *    has played it on, and `kingSlides` checks the map's size first, so all 25
 *    published counts are untouched by construction.
 *  - **Check and mate come out right for free.** A socked king is still the
 *    piece `gameStatus` looks for and still may not move into attack; it
 *    simply has more squares to try, so a mate against one is a genuine mate.
 *  - **It expires in `endTurn`, never in `passTurn`.** `legalMoves` makes and
 *    unmakes every candidate and `unmakeMove` cannot undo a deletion, so
 *    expiring on the clock would strip the slide while merely *generating* a
 *    move — the trap `expireArmour` is already written around.
 *
 * In rounds, as armour is (§31 T): three of *your* turns is three rounds,
 * whatever a turn holds.
 *
 * Gated on faction (§31 C6): Robotic Monarchies (`KING_FACTORY`) only.
 */
const programmerOil: Card = {
  id: "programmer-oil",
  name: "Programmer Oil",
  short: "Your kings move like queens",
  text: "Your kings move and attack like queens for three rounds. Robotic Monarchies only.",
  targeted: false,
  refuse: (state) =>
    state.position.kingKind[state.run!.side] === KING_FACTORY ? null : "not-robotic",
  apply: (state) => {
    const side = state.run!.side;
    // The one `socks` write outside `position.ts`, so it invalidates by hand —
    // the map's size and every scalar `moveStamp` watches are unchanged when a
    // side that already has socks plays a second pair.
    invalidateMoves(state.position);
    state.position.socks.set(
      side,
      state.position.round + RULES.programmerOilTurns
    );
  }
};


/**
 * Five squares of board, banked, spent on chains that join separated ground.
 *
 * **The Bomb's inverse, and not its mirror.** Deleting a cell can only ever
 * *shorten* a ray, so that card can break a check and can never give one.
 * Paving lengthens rays, so this one can do both — including opening a line
 * onto your own king. Every placement is therefore checked for self-check the
 * way a forge is (`requireKingSafe` in the `pave` case), which the Bomb still
 * never has to be.
 *
 * **Banked, not pasted.** Playing the card grants five and spends the card;
 * the ground goes down later, whenever you like.
 *
 * **A bridge, not a tile** (§26 C9). One `pave` lays a whole chain, because
 * "both ends touch ground" is a property of a chain and a single square has no
 * two ends. `refuseBridge` in `bridges.ts` holds every rule.
 */
const bridges: Card = {
  id: "bridges",
  name: "Bridges",
  short: "Bank five tiles to join ground",
  text: "Bank five board tiles. Spend them on chains joining ground that was separated.",
  targeted: false,
  refuse: () => null,
  apply: (state) => {
    const run = state.run!;
    run.tiles = (run.tiles ?? 0) + RULES.bridgeTiles;
  }
};

/**
 * One cardinal edge of the board, pushed out a square (§31 C7).
 *
 * The edge as it stands when played, column by column (`manifoldSquares`);
 * the direction rides `play-card.direction`. Every rule is in
 * `refuseManifold`, including the self-check, so the card cannot be offered
 * when the reducer would refuse it. Free, as laying ground is.
 */
const spatialManifold: Card = {
  id: "spatial-manifold",
  name: "Spatial Manifold",
  short: "Push one edge out a square",
  text: "Choose an edge of the board — north, east, south or west — and push it out by a square.",
  targeted: false,
  refuse: (state, play) =>
    play.direction === undefined
      ? "needs-a-direction"
      : refuseManifold(state.position, state.industry, state.run!.side, play.direction),
  apply: (state, play) => {
    // Recomputed rather than carried: `refuse` answered about this board.
    const grown = manifoldSquares(state.position, state.industry, play.direction!) as number[];
    setCells(state.position, [...state.position.cellList, ...grown]);
  }
};

/**
 * Crown one queen: she becomes a Matriarch, and a Matriarch is a foundry.
 *
 * **The card is the Signet Crown; the piece is still a Matriarch** (§26 C6).
 * A rename changes the id and keeps the mechanism, so everything the queen
 * becomes kept its name through that. **What she is stored as has since moved
 * again**: she is a `PIECE_VARIANTS` row (`MATRIARCH`), so the card is now the
 * one door onto a variant that no metal buys. `naming.ts` keeps Matriarch (a
 * factory role) apart from Empress (a royal rank), and the row supplies the
 * word — one fewer table to keep in step.
 *
 * **The sink is the point, not the button.** "She can forge" would be a
 * `canForge` carve-out and nothing else; what makes the card worth playing is
 * that the *economy* now knows about her. Filing her as a foundry is what does
 * it, because being one is already the answer `demandAt`, `canHoldUnit`,
 * `acceptsUnit` and `bufferCapacity` all give — so ore routes to her, lanes
 * aim at her and she saves for a recipe without a single carve-out saying
 * "except this queen".
 *
 * **It was stored in the industry, and the objection to `Position` was real:**
 * a crown there has to be migrated by `makeMove` and restored by `unmakeMove`,
 * and `legalMoves` makes and unmakes every candidate, so an imperfect undo
 * corrupts the board while merely *generating* a move. `Position.variant` is
 * that machinery — written, tested and already carrying four rows — so the
 * answer is to use it rather than to build a second one in `IndustryState`.
 *
 * **And she is a Monarch now** (§26.1 (10)), which the industry could never
 * have said: class is a fact about the piece, and the fences that read it are
 * handed a position. Not a king — check, mate and `royalsOf` are the crown's
 * and she has none — so what it buys is the Stasis, Bomb and Clasp exemptions.
 *
 * She keeps a queen's collecting sweep and gains a foundry's hopper — a
 * collector's bank is sized for what she picks up and hands straight on, and a
 * recipe has to be saved for.
 */
const signetCrown: Card = {
  id: "signet-crown",
  name: "Signet Crown",
  short: "Make a queen a Monarch who forges",
  text:
    "Robotic Monarchies only. One of your queens on an upgrade site becomes a Matriarch — " +
    "a Monarch who forges. Pays 3 electrum + 3 diamond and 2 fuel, like an upgrade.",
  targeted: true,
  refuse: (state, play) => {
    // A faction card (§31 C5), Programmer Oil's gate.
    if (state.position.kingKind[state.run!.side] !== KING_FACTORY) return "not-robotic";
    if (play.target === undefined) return "needs-a-target";
    const piece = pieceAt(state.position, play.target);
    if (!piece || pieceColor(piece) !== state.run!.side) return "not-your-piece";
    if (pieceType(piece) !== QUEEN) return "not-a-queen";
    // Silently re-crowning would spend a card for nothing, which is the sort
    // of refusal the UI has to be able to *say*.
    if (isMatriarch(state.position, play.target)) return "already-crowned";
    // **A queen wears one row, and the crown is a row now.** The bought-once
    // rule `canTakeVariant` applies to metal applies here too — a Joan-Pattern
    // Queen sold her factory for the guns, and a card must not sell it back.
    if (variantAt(state.position, play.target) !== VARIANT_PLAIN) return "no-factory";
    // **An upgrade in all but the door** (§31 C5): the site, the row's bill
    // and the fuel, by `upgrade`'s own rule (`upgrades.ts`).
    return upgradeCostError(state.position, state.industry, play.target, state.run!.side, MATRIARCH);
  },
  apply: (state, play) => {
    payVariant(state.position, state.industry, play.target!, state.run!.side, MATRIARCH);
    setVariant(state.position, play.target!, MATRIARCH);
  }
};

/** Is a Monarch-class piece of this side on one of the eight squares around `at`? */
const monarchBeside = (pos: Position, at: number, side: Side): boolean =>
  KING_DIRS.some((offset) => {
    const sq = at + offset;
    // `touches`, never the offset alone: an offset at the file edge carries into the rank.
    if (!touches(sq, at)) return false;
    const piece = pieceAt(pos, sq);
    return piece !== 0 && pieceColor(piece) === side && pieceClassAt(pos, sq) === CLASS_MONARCH;
  });

/**
 * A Power piece beside a Monarch **becomes the faction's default Monarch**
 * (§31 C3) — the King until the other factions land (X). Not a royal keeping
 * its moves: the rank, row and facing go; armor moves by the ranks' base
 * difference, a promotion's rule; health taken stays taken. `crowned` is no
 * longer written by anything.
 */
const coronation: Card = {
  id: "coronation",
  name: "Coronation",
  short: "Make a piece beside your king a king",
  text:
    "One of your Power pieces standing beside a Monarch of yours becomes a king. " +
    "Your army cannot be mated while two stand.",
  targeted: true,
  refuse: (state, play) => {
    if (play.target === undefined) return "needs-a-target";
    const piece = pieceAt(state.position, play.target);
    const side = state.run!.side;
    if (!piece || pieceColor(piece) !== side) return "not-your-piece";
    const cls = pieceClassAt(state.position, play.target);
    if (cls === CLASS_MONARCH) return "already-royal";
    if (cls !== CLASS_POWER) return "not-a-power-piece";
    if (!monarchBeside(state.position, play.target, side)) return "no-monarch-beside";
    return null;
  },
  // No turn cost, and no `requireKingSafe`: the piece does not move.
  apply: (state, play) => {
    const pos = state.position;
    const sq = play.target!;
    const side = state.run!.side;
    const was = pieceType(pieceAt(pos, sq));
    setPiece(pos, sq, makePiece(side, KING));
    setVariant(pos, sq, VARIANT_PLAIN);
    setFacing(pos, sq, 0);
    // The second life: `bindingRoyal` answers -1 while a spare stands.
    addKing(pos, side, sq);
    reframePlating(pos, sq, PIECE_PLATING[KING] - PIECE_PLATING[was]);
  }
};

/**
 * IFF Spoofer — one of yours flies somebody else's transponder code.
 *
 * Renamed from Disguise, and the mechanism moved with it (§26 C12): the
 * impersonated faction **cannot capture the wearer** (§31 C9: that faction
 * alone, not its allies), and the wearer **can give check to its owner's own
 * Monarchs** (`spoofAttacks`). `campaign/disguise.ts` holds the why.
 *
 * The two-square shape is the Hydraulic Clasp's: the card is about a
 * *relationship*, so one target could only mean "as whatever is nearest".
 */
const iffSpoofer: Card = {
  id: "iff-spoofer",
  name: "IFF Spoofer",
  short: "Dress a piece in enemy colours",
  text:
    "One of your pieces flies an enemy's colours for five rounds. That army " +
    "cannot capture it until it takes something of theirs — and it can check your own king.",
  targeted: true,
  refuse: (state, play) => {
    if (play.target === undefined || play.source === undefined) return "needs-a-target";
    const piece = pieceAt(state.position, play.target);
    if (!piece || pieceColor(piece) !== state.run!.side) return "not-your-piece";
    const source = pieceAt(state.position, play.source);
    if (!source) return "nothing-to-copy";
    // Only an enemy's: an ally's colours buy immunity from somebody who could
    // not take you anyway, and your own are a costume with no wearer.
    if (!areEnemies(state.position, pieceColor(piece), pieceColor(source))) {
      return "not-an-enemy";
    }
    // There is exactly one of them, so a second is a claim anybody can check.
    if (pieceType(source) === KING) return "not-a-king";
    // One at a time, or re-dressing a spy turns a card with a clock into one
    // with none.
    if (disguisedAs(state.position, play.target)) return "already-disguised";
    return null;
  },
  apply: (state, play) => {
    const source = pieceAt(state.position, play.source!);
    wearDisguise(
      state.position,
      play.target!,
      pieceColor(source),
      pieceType(source)
    );
  }
};

/**
 * Clamp two pieces standing beside each other, so moving one drags the other.
 *
 * Replaces Imitate (§26 C11). Three rules about the play: at least one end is
 * yours (an enemy dragged out of position is the point of it); neither may be
 * under a Stasis Field, refused again in `dragOf` because two cards in one
 * turn is legal; and one clamp at a time. **Monarchs allowed** (§31 C8).
 *
 * It lasts **the turn** (§31 C8): `followClasp` carries it with its two bodies
 * after each move, `expireClasp` drops it at End Turn.
 *
 * `touches` rather than `KING_DIRS`: a packed offset wraps at the file band.
 */
const hydraulicClasp: Card = {
  id: "hydraulic-clasp",
  name: "Hydraulic Clasp",
  short: "Chain two pieces so one drags the other",
  text:
    "Clamp two pieces standing beside each other — one of them yours. " +
    "For the rest of this turn, moving one drags the other the same way.",
  targeted: true,
  refuse: (state, play) => {
    if (play.target === undefined || play.source === undefined) return "needs-a-target";
    const position = state.position;
    const a = pieceAt(position, play.target);
    const b = pieceAt(position, play.source);
    if (!a || !b) return "not-your-piece";
    // At least one end is yours; whose the other end is says nothing.
    if (pieceColor(a) !== state.run!.side && pieceColor(b) !== state.run!.side) {
      return "not-your-piece";
    }
    if (!touches(play.target, play.source)) return "not-beside-it";
    if (armourLeft(position, play.target) > 0 || armourLeft(position, play.source) > 0) {
      return "already-locked";
    }
    if (position.clasp) return "already-clasped";
    return null;
  },
  apply: (state, play) => {
    setClasp(state.position, play.target!, play.source!, state.run!.side);
  }
};

/**
 * A round handed back — your last turn, and every reply to it (§31 C11).
 *
 * **The one card whose effect is not in this file, and cannot be.** A rewind is
 * an operation on the *log*: `apply` is handed an already-cloned `GameState`
 * and the reducer has never seen a command list, which is the same split that
 * keeps `GameStore.undo` out of `src/engine/rules/` altogether. So the whole of it
 * lives in `src/engine/game/rewind.ts` beside the store that owns the log, and what
 * reaches the log is a `burn-card` on the board the rewind lands on.
 *
 * **So `refuse` says no, always, and that is a guard rather than an oversight.**
 * The alternative is an `apply` that does nothing, which spends the card and
 * hands back a board that has not moved — a silent no-op, and the one failure
 * mode this interaction layer is written throughout to refuse. Answering no
 * makes the ordinary door fail loudly, so the only way to play this card is
 * the one that works.
 *
 * It does **not** cost the turn, which is the whole point: a rewind that spent
 * the turn would hand it straight back to the army you rewound. What pays for
 * it is that the card is gone.
 */
const timeFluxxer: Card = {
  id: "time-fluxxer",
  name: "Time Fluxxer",
  // **Said against Undo, because a player has both.** Undo takes back one
  // thing at a time and stops at the start of the turn you are standing in;
  // this is the reach Undo does not have — the whole of your *previous* turn,
  // every answer to it undone with it. Words that did not say which was which
  // made the card read as a button already on the screen.
  short: "Take back the previous round",
  text:
    "Go back to the start of your turn in the previous round, with every army's answer to it. " +
    "Undo reaches only into the turn you are in; this reaches past it.",
  targeted: false,
  refuse: () => "rewind-only",
  apply: () => {
    // Unreachable: `refuse` never returns null. Kept because `Card` requires
    // it, and left empty on purpose — anything here would be an effect that
    // looks live and never runs.
  }
};

/**
 * Two fewer moves this turn; the factory ticks thrice for each (§31 C1).
 *
 * **Flat**: always `yearningMoves × yearningTicks` (6) ticks, and the moves
 * floor at nought — a one-move turn plays it too, rather than the card being
 * dead until a Relic stands. Needs a move left (`spendMove`'s `requireMove`),
 * which is the one it spends; `apply` takes the other.
 */
const yearningForTheMines: Card = {
  id: "yearning-for-the-mines",
  name: "Yearning for the Mines",
  short: "Two fewer moves, six factory ticks",
  text:
    `Take ${RULES.yearningMoves} fewer moves this turn. Your factory runs ` +
    `${RULES.yearningTicks} times for each.`,
  // The reducer's one `spendMove` is one of the two moves given up.
  spendsTurn: true,
  targeted: false,
  refuse: () => null,
  apply: (state) => {
    const side = state.run!.side;
    state.position.movesLeft = Math.max(0, state.position.movesLeft - (RULES.yearningMoves - 1));
    spendTicks(state, side, RULES.yearningMoves * RULES.yearningTicks);
  }
};

/**
 * Three Off-Turn Production points, for the next rollover only (§31 C4).
 *
 * The doc's "extra factory ticks next turn": one-shot points beside the
 * standing `offTurnPerSide`, consumed by the round rollover in `endTurn`
 * (× enemy factions alive last round, the stat's own rule). Needs an enemy on
 * the board, or the points would scale to nothing.
 */
const enemyAtTheGates: Card = {
  id: "the-enemy-is-at-the-gates",
  name: "The Enemy is at the Gates",
  short: `+${RULES.gatesPoints} Off-Turn Production next round`,
  text:
    `Gain ${RULES.gatesPoints} Off-Turn Production points: when the round rolls over, your ` +
    "factory ticks that many times for each enemy army that was on the board.",
  targeted: false,
  refuse: (state) => {
    const side = state.run!.side;
    return livingSides(state.position).some((other) => areEnemies(state.position, side, other))
      ? null
      : "no-enemies";
  },
  apply: (state) => {
    state.run = withOffTurnOnce(state.run!, state.run!.side, RULES.gatesPoints);
  }
};

/** Wall up to three squares: nothing may move onto or through them, for good (§31 C10). */
const staticMoat: Card = {
  id: "static-moat",
  name: "Static Moat",
  short: "Wall off a row of squares",
  text:
    "Wall up to three squares in a row. Nothing may move onto or through " +
    "them, whoever it belongs to. It does not wear off.",
  targeted: true,
  refuse: (state, play) => {
    const squares = play.pieces ?? (play.target !== undefined ? [play.target] : []);
    if (squares.length === 0) return "needs-a-target";
    return refuseMoat(state.position, squares);
  },
  apply: (state, play) => {
    const squares = play.pieces ?? [play.target!];
    placeMoat(state.position, squares);
  }
};

export const CARD_TABLE: Record<CardId, Card> = {
  "reactor-boosters": reactorBoosters,
  "full-production-gambit": fullProductionGambit,
  "stasis-field": stasisField,
  "micro-singularity-bomb": microSingularityBomb,
  "programmer-oil": programmerOil,
  "bridges": bridges,
  "signet-crown": signetCrown,
  "hydraulic-clasp": hydraulicClasp,
  "iff-spoofer": iffSpoofer,
  "time-fluxxer": timeFluxxer,
  "yearning-for-the-mines": yearningForTheMines,
  coronation,
  "spatial-manifold": spatialManifold,
  "static-moat": staticMoat,
  "the-enemy-is-at-the-gates": enemyAtTheGates,
  march
};

/**
 * Every card, as a list, for anything that offers them.
 *
 * **A second list, and that is what makes it dangerous.** `CARD_TABLE` is what
 * the reducer looks a card up in; this is what the dev menu and every future
 * picker iterates. A card in the table and not here is playable by the rules
 * and reachable by nothing — which is how Long Socks and Free Space shipped
 * with reducer cases, tests and no way to hold one. `cards.test.ts` walks the
 * two and fails on a card missing from either.
 */
export const CARD_LIST: readonly Card[] = [
  reactorBoosters,
  fullProductionGambit,
  stasisField,
  microSingularityBomb,
  programmerOil,
  bridges,
  signetCrown,
  hydraulicClasp,
  iffSpoofer,
  timeFluxxer,
  yearningForTheMines,
  coronation,
  spatialManifold,
  staticMoat,
  enemyAtTheGates,
  march
];

// `armourLeft` moved to `position.ts` (§26 C4), not re-exported: `attacks.ts` would cycle.

